import { useMemo } from 'react'
import { ChevronRight, FileText } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { useUserRole } from '@/hooks/useUserRole'
import { cn } from '@/lib/utils/helpers'
import {
  ReportHubNavLink,
  canRenderReportHubLink,
} from '../components/ReportHubNavLink'
import { useReportDescriptionMap } from '../hooks/useReportDescriptionMap'
import { VISIBLE_REPORT_HUB_LINKS } from '../constants/reportHubLinks'
import { REPORT_HUB_CATEGORIES } from '../constants/reportHubCategories'
import { HUB_COLUMN_ORDER, LIBERTY_ORG_ID } from '../constants/reportHub'
import type {
  ReportHubCategoryId,
  ReportHubCategoryMeta,
  ReportHubLink,
  ReportsHubVisibleGroup,
} from '../types'
import styles from './ReportsHubPage.module.css'

const ICON_TONE_CLASS: Record<string, string> = {
  green: styles.iconGreen,
  purple: styles.iconPurple,
  blue: styles.iconBlue,
  amber: styles.iconAmber,
  pink: styles.iconPink,
  teal: styles.iconTeal,
}

const LINK_ICON_TONE_CLASS: Record<string, string> = {
  green: styles.linkIconGreen,
  purple: styles.linkIconPurple,
  blue: styles.linkIconBlue,
  amber: styles.linkIconAmber,
  pink: styles.linkIconPink,
  teal: styles.linkIconTeal,
}

function isLinkVisible(
  link: ReportHubLink,
  hostname: string,
  orgId: number | null | undefined,
  hasPolicy: (keys: string[]) => boolean,
): boolean {
  if (link.hidden) return false
  if (!hasPolicy(link.policies)) return false
  if (link.requiresLibertyOrg && orgId !== LIBERTY_ORG_ID) return false
  if (link.hideOnHostnames?.includes(hostname)) return false
  return true
}

function CategoryCard({
  category,
  links,
  getTooltip,
  fillHeight = false,
}: {
  category: ReportHubCategoryMeta
  links: ReportHubLink[]
  getTooltip: (key: string | undefined) => string | undefined
  fillHeight?: boolean
}) {
  return (
    <section
      className={cn(styles.card, fillHeight && styles.cardFill)}
      data-testid={`reports-hub-category-${category.id}`}
    >
      <div className={styles.cardHeader}>
        <span
          className={cn(styles.iconBadge, ICON_TONE_CLASS[category.tone])}
          aria-hidden="true"
        >
          <category.icon />
        </span>
        <h2 className={styles.cardTitle}>{category.label}</h2>
        <span className={styles.count}>{links.length}</span>
      </div>

      <ul className={styles.linksList}>
        {links.map((link) => (
          <li
            key={`${link.label}-${link.kind}-${link.reactPath ?? link.dashboardPath ?? link.externalHref ?? ''}`}
            className={styles.linkItem}
          >
            <ReportHubNavLink
              link={link}
              tooltip={getTooltip(link.descriptionKey)}
              className={styles.linkRow}
            >
              <FileText
                className={cn(styles.linkIcon, LINK_ICON_TONE_CLASS[category.tone])}
                aria-hidden="true"
              />
              <span className={styles.linkLabel}>{link.label}</span>
              <ChevronRight className={styles.chevron} aria-hidden="true" />
            </ReportHubNavLink>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * Reports hub — all category sections sit in one outer card.
 * Financial stretches to match height of the other columns.
 */
export function ReportsHubPage() {
  const { hasPolicy, orgId } = useUserRole()
  const { getTooltip, loading: descriptionsLoading } = useReportDescriptionMap()
  const hostname = typeof window !== 'undefined' ? window.location.hostname : ''

  const visibleLinks = useMemo(
    () =>
      VISIBLE_REPORT_HUB_LINKS.filter(
        (link) =>
          isLinkVisible(link, hostname, orgId, hasPolicy) &&
          canRenderReportHubLink(link),
      ),
    [hostname, orgId, hasPolicy],
  )

  const visibleGroups = useMemo(
    () =>
      REPORT_HUB_CATEGORIES.map((category) => ({
        category,
        links: visibleLinks.filter((link) => link.category === category.id),
      })).filter((group) => group.links.length > 0),
    [visibleLinks],
  )

  const columns = useMemo(() => {
    const byId = new Map(
      visibleGroups.map((group) => [group.category.id, group] as const),
    )
    const placed = new Set<ReportHubCategoryId>()
    const cols: ReportsHubVisibleGroup[][] = HUB_COLUMN_ORDER.map((ids) =>
      ids
        .map((id) => {
          const group = byId.get(id)
          if (group) placed.add(id)
          return group
        })
        .filter((group): group is ReportsHubVisibleGroup => Boolean(group)),
    ).filter((col) => col.length > 0)

    const rest = visibleGroups.filter((g) => !placed.has(g.category.id))
    if (rest.length > 0) cols.push(rest)
    return cols
  }, [visibleGroups])

  const showNoReport = orgId != null && orgId !== LIBERTY_ORG_ID

  return (
    <PageShell title="All Reports">
      {showNoReport && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-white px-6 py-5 shadow-sm">
          <p className="text-base text-slate-800">No Report</p>
        </div>
      )}

      <div className={styles.panel} data-testid="reports-hub-panel">
        {descriptionsLoading && visibleLinks.length === 0 ? (
          <ModuleScreenLoading
            variant="inline"
            className="py-8"
            data-testid="reports-hub-loading"
          />
        ) : visibleGroups.length === 0 ? (
          <p className="text-sm text-slate-500">
            No reports are available for your role.
          </p>
        ) : (
          <div className={styles.grid} data-testid="reports-hub-grid">
            {columns.map((column, colIndex) => {
              const stretch = column.length === 1
              return (
                <div
                  key={`reports-hub-col-${colIndex}`}
                  className={cn(styles.column, stretch && styles.columnStretch)}
                  data-testid={`reports-hub-column-${colIndex}`}
                >
                  {column.map(({ category, links }) => (
                    <CategoryCard
                      key={category.id}
                      category={category}
                      links={links}
                      getTooltip={getTooltip}
                      fillHeight={stretch}
                    />
                  ))}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </PageShell>
  )
}
